// Pure state machine + timing helpers for the web-page signage tile — shared
// by the web player's iframe zone (zone-web.tsx) and the native shells'
// WebView tile (signage-native). Kept DOM/RN-free so the
// refresh/timeout/fallback behavior is unit-testable.

/** Give a slow dashboard this long to fire onload before falling back. */
export const WEB_LOAD_TIMEOUT_MS = 15_000;
/** After a failure, retry the page this often (fallback stays up meanwhile). */
export const WEB_RETRY_MS = 60_000;
/** Configured refreshSec below this is ignored (protects the embedded site). */
export const MIN_REFRESH_SEC = 10;

export type WebTileStatus = "loading" | "loaded" | "failed";

export interface WebTileState {
  status: WebTileStatus;
  /** Bumps the iframe key: each bump forces a full re-mount (re-load). */
  refreshCount: number;
}

export type WebTileEvent =
  /** New content target (id/url changed): start over. */
  | "reset"
  /** iframe onload fired. */
  | "load"
  /** iframe onerror fired. */
  | "error"
  /** Load-timeout elapsed while still loading. */
  | "timeout"
  /** Periodic refresh (refreshSec) or post-failure retry tick. */
  | "refresh";

export const initialWebTileState: WebTileState = {
  status: "loading",
  refreshCount: 0,
};

export function nextWebTileState(
  state: WebTileState,
  event: WebTileEvent,
): WebTileState {
  switch (event) {
    case "reset":
      return { status: "loading", refreshCount: 0 };
    case "load":
      return state.status === "loading"
        ? { ...state, status: "loaded" }
        : state;
    case "error":
      return { ...state, status: "failed" };
    case "timeout":
      // Only meaningful while still loading; a loaded/failed tile ignores it.
      return state.status === "loading"
        ? { ...state, status: "failed" }
        : state;
    case "refresh":
      return { status: "loading", refreshCount: state.refreshCount + 1 };
  }
}

/**
 * Milliseconds between forced reloads for a loaded tile, or null when the
 * page should load once and stay. Sub-minimum configs are treated as unset.
 */
export function refreshDelayMs(
  refreshSec: number | null | undefined,
): number | null {
  if (refreshSec == null || refreshSec < MIN_REFRESH_SEC) return null;
  return refreshSec * 1000;
}

/**
 * The iframe element key: changing it re-mounts the iframe, which is the only
 * reliable cross-origin way to force a fresh page load.
 */
export function iframeKey(contentId: string, refreshCount: number): string {
  return `${contentId}:${refreshCount}`;
}

/**
 * Human subtitle for on-air web/widget signage in now-playing (CB-023/027):
 * "Web page", "Widget", or "Widget · Clock". The screen bus has no artist, so
 * this rides the now-playing `artist` field to describe the surface a viewer
 * sees on the control console.
 */
export function signageNowPlayingLabel(content: {
  kind: "web" | "widget";
  widgetKind?: string | null;
}): string {
  if (content.kind === "widget") {
    const w = content.widgetKind;
    return w ? `Widget · ${w.charAt(0).toUpperCase()}${w.slice(1)}` : "Widget";
  }
  return "Web page";
}

/**
 * Origin pin for the native WebView tile: only navigations inside the initial
 * URL's origin are allowed (the embedded page cannot hijack the kiosk by
 * navigating the top frame elsewhere). Unparseable URLs are rejected.
 */
export function sameOriginAsTile(tileUrl: string, requestUrl: string): boolean {
  try {
    const a = new URL(tileUrl);
    const b = new URL(requestUrl);
    // about:blank / about:srcdoc happen during mount teardown; allow them.
    if (b.protocol === "about:") return true;
    return a.origin === b.origin;
  } catch {
    return false;
  }
}
